import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { MdKeyboardArrowRight } from 'react-icons/md';
import axiosInstance from 'config/axiosInstance';
import { getBusinessTimeZone } from 'config/time';
import false_icon from '../../assets/icons/false.svg';
import true_icon from '../../assets/icons/true.svg';
import { useTranslation } from 'react-i18next';
import i18n from 'config/i18n';
import FetchFailed from 'reusable/FetchFailed';
import DeviceTimeZone, { deviceTimeZoneOf } from 'reusable/DeviceTimeZone';

const VehicleInspectionDetails: React.FC = () => {
  const { t } = useTranslation('vehicle');
  const { id } = useParams<{ id: string }>();
  const [inspectionDetail, setInspectionDetails] = useState<any>(null);
  // ADM-15。取得の失敗を、空の表と区別して出す。
  const [fetchError, setFetchError] = useState<unknown>(null);

  const fetchData = useCallback(async () => {
    try {
      let apiUrl = `/v1/vehicle_inspection_records/${id}/`;
      const response = await axiosInstance.get(apiUrl);
      setInspectionDetails(response.data);
      setFetchError(null);
    } catch (error) {
      console.error('Failed to fetch data inspection detail', error);
      setFetchError(error);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    // EV-004。ブラウザの時間帯ではなく、業務時間帯（会社の時間帯。EV-116）の業務日で表示する（一覧と同じ）。
    const formatter = new Intl.DateTimeFormat('ja-JP', {
      timeZone: getBusinessTimeZone(),
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    const parts = formatter.formatToParts(date);
    const formattedDate = `${parts.find(part => part.type === 'year')?.value}年${parts.find(part => part.type === 'month')?.value}月${parts.find(part => part.type === 'day')?.value}日`;
    return formattedDate;
  };
  console.log(inspectionDetail, i18n.language);
  return (
    <section className="flex flex-col space-y-4 p-6">
      <div className="flex flex-row space-x-4 items-center font-semi-bold text-primary/300 py-2">
        <a href="/vehicle">
          <p>{t('inspection-record.title')}</p>
        </a>
        <MdKeyboardArrowRight size={24} />
        <p>{inspectionDetail ? inspectionDetail.vehicle.plate_number : '-'}</p>
        <MdKeyboardArrowRight size={24} />
        <p className="underline">{t('inspection-record.details')}</p>
      </div>
      {fetchError ? (
        <FetchFailed
          error={fetchError}
          onRetry={fetchData}
          message={t('ui.detail-fetch-failed', { ns: 'common' })}
        />
      ) : null}
      <div className='bg-white rounded-md p-4'>
        <div className='bg-primary/50 rounded-md flex flex-row p-2'>
          <h6 className='flex-1 text-left'>{t('all-vehicle.vehicle-profile.detail-page.date')}</h6>
          <h6 className='flex-1 text-center'>{t('all-vehicle.vehicle-profile.detail-page.driver-name')}</h6>
          <h6 className='flex-1 text-center'>{t('all-vehicle.vehicle-profile.detail-page.maintenance')}</h6>
          <h6 className='flex-1 text-center'>{t('all-vehicle.vehicle-profile.detail-page.operation')}</h6>
          <h6 className='flex-1 text-center'>{t('time-zone.device', { ns: 'common' })}</h6>
        </div>
        <div className='flex flex-row p-2'>
          <p className='flex-1 text-left'>{inspectionDetail ? formatDate(inspectionDetail.create_time) : '-'}</p>
          <p className='flex-1 text-center'>{inspectionDetail?.user?.name}</p>
          <p className='flex-1 text-center'>{inspectionDetail?.maintenance_manager}</p>
          <p className='flex-1 text-center'>{inspectionDetail?.operations_manager}</p>
          {/* EV-116 段階3。送信の時点の端末の時間帯（詳細では名前も並べる）。 */}
          <p className='flex-1 text-center'>
            {inspectionDetail ? (
              <DeviceTimeZone {...deviceTimeZoneOf(inspectionDetail)} at={inspectionDetail.create_time} detail />
            ) : null}
          </p>
        </div>
      </div>
      <div className="bg-white rounded-md p-4">
        <table className="w-full">
          <thead className="bg-primary/50 rounded-md">
            <tr>
              <th className="w-2/12 text-left pl-8 py-2 rounded-l-md">{t('all-vehicle.vehicle-profile.detail-page.vehicle-part')}</th>
              <th className="w-3/12 text-left">{t('all-vehicle.vehicle-profile.detail-page.vehicle-item')}</th>
              <th className="w-3/12 text-center">{t('all-vehicle.vehicle-profile.detail-page.inspection-status')}</th>
              <th className="w-4/12 text-center rounded-r-md">{t('all-vehicle.vehicle-profile.detail-page.note')}</th>
            </tr>
          </thead>
          <tbody>
            {inspectionDetail?.details?.vehicle_part?.map((part: any) =>
              part.item.map((item: any, index: number) => (
                <tr key={item.name}>
                  {index === 0 && (
                    <td
                      rowSpan={part.item.length}
                      className="text-center align-middle border-b"
                    >
                      {t(`parts.${part.name.trim()}`, { defaultValue: part.name })}
                    </td>
                  )}
                  <td className="text-left border-b py-3"> {t(`parts.${item.name.trim()}`, { defaultValue: item.name })}</td>
                  <td className="text-center border-b">
                    <img
                      className="h-6 mx-auto"
                      src={item.status ? true_icon : false_icon}
                      alt={item.status ? t('ui.check-ok', { ns: 'common' }) : t('ui.check-ng', { ns: 'common' })}
                    />
                  </td>
                  <td className="text-center border-b">{item.note || '-'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div className="flex flex-col space-y-2">
        <h6 className="font-semi-bold">{t('all-vehicle.vehicle-profile.detail-page.suggestions')}</h6>
        {/* EV-278。アプリが送りサーバーが保存する最上位の suggest_or_concern を出す（以前は
            誰も書かない details の中を読み、常に「提案や懸念はありません」だった）。入力欄は
            複数行なので改行を保つ。空・無い（項目を足す前の記録）は既存の文言。 */}
        <p className="bg-white rounded-sm p-2 whitespace-pre-wrap">
          {inspectionDetail?.suggest_or_concern || `${t('parts.No-suggestions-or-concerns')}`}
        </p>
      </div>
    </section>
  );
};

export default VehicleInspectionDetails;